import { FC, useState } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleCheckmarkFullIcon,
  CircleCheckmarkLineIcon,
  IconSizes
} from '@inno-ui/icons'
import cn from 'classnames'
import { MediaCarousel } from 'modules/download-center/components/MediaCarousel'
import { formatBytes } from 'modules/download-center/utils/formatBytes'
import { formatDate } from 'modules/download-center/utils/formatDate'
import {
  getAppIconFallback,
  handleImageFallback
} from 'modules/download-center/utils/imageFallback'
import {
  pickLocalizedList,
  pickLocalizedText
} from 'modules/download-center/utils/pickLocalizedText'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetDownloadableAppQuery } from 'shared/api/containerApi'

import styles from './DownloadCenterDetailPage.module.scss'
import { DownloadButton } from './components/DownloadButton'

const VISIBLE_CHANGELOG_ENTRIES = 1

export const DownloadCenterDetailPage: FC = () => {
  const { t, i18n } = useTranslation()
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const { data: app, isLoading } = useGetDownloadableAppQuery(appId)
  const [showAllChangelog, setShowAllChangelog] = useState(false)

  if (!app) {
    return (
      <div className={styles.page}>
        <div className={styles.backLink}>
          <Button
            label={t('downloadCenter.detailPage.back')}
            variant={ButtonVariants.Outline}
            icon={ChevronLeftIcon}
            onClick={() => navigate('/download')}
          />
        </div>
        <p className={styles.status}>
          {isLoading
            ? t('downloadCenter.detailPage.loading')
            : t('downloadCenter.detailPage.notFound')}
        </p>
      </div>
    )
  }

  const primaryPlatform = app.platforms[0]
  const changelog = app.changelog ?? []
  const visibleChangelog = showAllChangelog
    ? changelog
    : changelog.slice(0, VISIBLE_CHANGELOG_ENTRIES)
  const description = pickLocalizedText(app.description, i18n.language)
  const about = pickLocalizedList(app.about, i18n.language)

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <nav className={styles.breadcrumb} aria-label='Breadcrumb'>
          <img src='/Vector.svg' alt='' width={16} height={16} />
          <a
            href='/download'
            onClick={e => {
              e.preventDefault()
              navigate('/download')
            }}
          >
            {t('downloadCenter.detailPage.breadcrumbHome')}
          </a>
          <ChevronRightIcon size={IconSizes.Size14} color='#999999' />
          <span>{app.name}</span>
        </nav>
        <Button
          label={t('downloadCenter.detailPage.back')}
          variant={ButtonVariants.Grey}
          icon={ChevronLeftIcon}
          onClick={() => navigate('/download')}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.header}>
          <img
            src={app.icon || getAppIconFallback(app.name)}
            alt=''
            className={styles.icon}
            onError={e => handleImageFallback(e, getAppIconFallback(app.name))}
          />
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{app.name}</h1>
            {description && <p className={styles.description}>{description}</p>}
          </div>
        </div>

        <div className={styles.headerActions}>
          <DownloadButton platforms={app.platforms} />
        </div>

        {primaryPlatform?.is_signed && (
          <span className={styles.signedMeta}>
            <CircleCheckmarkLineIcon className={styles.signedIcon} />
            {t('downloadCenter.detailPage.signedBadge')}
          </span>
        )}

        <div className={styles.divider} />

        <section className={styles.section}>
          <MediaCarousel screenshots={app.screenshots} videos={app.videos} />
        </section>

        {!!about.length && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('downloadCenter.detailPage.aboutTitle')}
            </h2>
            <div className={styles.about}>
              {about.map((paragraph, i) => (
                <p key={i} className={styles.aboutParagraph}>
                  {paragraph}
                </p>
              ))}
            </div>
          </section>
        )}

        {!!app.features?.length && (
          <ul className={styles.featureList}>
            {app.features.map(feature => {
              const featureTitle = pickLocalizedText(
                feature.title,
                i18n.language
              )
              return (
                <li key={featureTitle} className={styles.featureRow}>
                  <CircleCheckmarkFullIcon
                    className={styles.featureCheck}
                    color='var(--grey-500, #cccccc)'
                  />
                  <div className={styles.featureContent}>
                    <div className={styles.featureTitle}>{featureTitle}</div>
                    <div className={styles.featureDescription}>
                      {pickLocalizedText(feature.description, i18n.language)}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {!!(app.version || primaryPlatform?.size_bytes || app.released_at) && (
          <p className={styles.versionMeta}>
            {t('downloadCenter.detailPage.versionMeta', {
              version: app.version ?? '',
              size: primaryPlatform?.size_bytes
                ? formatBytes(primaryPlatform.size_bytes)
                : '',
              date: app.released_at ? formatDate(app.released_at) : ''
            })}
          </p>
        )}
      </div>

      <div className={styles.card}>
        <h2 className={cn(styles.sectionTitle, styles.changelogTitle)}>
          {t('downloadCenter.detailPage.changelogTitle')}
        </h2>
        {!changelog.length && (
          <p className={styles.status}>
            {t('downloadCenter.detailPage.noChangelog')}
          </p>
        )}
        {!!changelog.length && (
          <>
            <ul className={styles.changelog}>
              {visibleChangelog.map(entry => (
                <li key={entry.version} className={styles.changelogEntry}>
                  <div className={styles.changelogHeader}>
                    <div>
                      <span className={styles.changelogVersion}>
                        {entry.version}
                      </span>
                      <span className={styles.changelogDate}>
                        {formatDate(entry.date) ?? entry.date}
                      </span>
                    </div>
                  </div>
                  <ul className={styles.changelogNotes}>
                    {entry.notes.map((note, i) => (
                      <li key={i} className={styles.changelogNote}>
                        {pickLocalizedText(note.text, i18n.language)}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>

            {changelog.length > VISIBLE_CHANGELOG_ENTRIES && (
              <button
                type='button'
                className={styles.showMoreButton}
                onClick={() => setShowAllChangelog(current => !current)}
              >
                {showAllChangelog
                  ? t('downloadCenter.detailPage.showLess')
                  : t('downloadCenter.detailPage.showMore')}
                <ChevronDownIcon
                  size={IconSizes.Size14}
                  className={cn(styles.showMoreIcon, {
                    [styles.showMoreIconOpen]: showAllChangelog
                  })}
                />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
